import React, { useState, useEffect } from 'react';
import { 
	Box, 
	Typography, 
	Button, 
	Paper, 
	TextField, 
	Alert,
	Stepper,
	Step,
	StepLabel,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Checkbox,
	InputAdornment,
	IconButton,
	useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowForward, ArrowBack } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { registerUser, clearError } from '../../store/slices/authSlice';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

interface FormData {
	// Business Information
	area: string;
	pointOfSale: string;
	accountType: string;
	businessProfile: string;
	
	// Client Information
	fullName: string;
	companyName: string;
	completeAddress: string;
	authorizePersonName: string;
	authorizePersonDesignation: string;
	authorizePersonPhone: string;
	authorizePersonCNIC: string;
	adminContactName: string;
	adminPhoneNumber: string;
	financeContactName: string;
	webAddress: string;
	brandName: string;
	ntnCNIC: string;
	gst: string;
	businessSector: string;
	products: string;
	
	// Bank Details
	bankAccountTitle: string;
	bankName: string;
	bankIBAN: string;
	bankCodeAccountNumber: string;
	
	// Operation Information
	pickupAddress: string;
	contactPerson: string;
	phoneNumber: string;
	emailAddress: string;
	
	// Login Information
	password: string;
	confirmPassword: string;
	termsAccepted: boolean;
}

type FormErrors = { [K in keyof FormData]?: string };

const steps = [
	'Business Information',
	'Client Details', 
	'Bank Information',
	'Operation Details',
	'Account Setup'
];

const areas = [
	'LAHORE', 'KASUR', 'FAISALABAD', 'BHAI PHERU', 'PATTOKI', 
	'NANKANA SAHIB', 'Shakargarh'
];

const banks = [
	'Al Baraka Bank (Pakistan) Limited',
	'Allied Bank Limited',
	'Askari Islamic Bank',
	'Bank Alfalah Limited',
	'Bank Al-Habib Limited',
	'BankIslami Pakistan Limited',
	'Citi Bank',
	'Deutsche Bank A.G.',
	'The Bank of Tokyo-Mitsubishi UFJ',
	'Dubai Islamic Bank Pakistan Limited',
	'Faysal Bank Limited',
	'Jazz Cash Bank Limited',
	'First Women Bank Limited',
	'Habib Bank Limited',
	'Standard Chartered Bank (Pakistan) Limited',
	'Habib Metropolitan Bank Limited',
	'Industrial and Commercial Bank of China',
	'Industrial Development Bank of Pakistan',
	'JS Bank Limited',
	'MCB Bank Limited',
	'MCB Islamic Bank Limited',
	'Meezan Bank Limited',
	'National Bank of Pakistan',
	'Bank of Punjab',
	'Sindh Bank',
	'Bank of Khyber',
	'Zarai Taraqiyati Bank Limited',
	'Bank of Azad Jammu & Kashmir'
];

const Register = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const muiTheme = useTheme();
	const { mode } = useAppTheme();
	const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

	const [activeStep, setActiveStep] = useState(0);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		area: '',
		pointOfSale: '',
		accountType: '',
		businessProfile: '',
		fullName: '',
		companyName: '',
		completeAddress: '',
		authorizePersonName: '',
		authorizePersonDesignation: '',
		authorizePersonPhone: '',
		authorizePersonCNIC: '',
		adminContactName: '',
		adminPhoneNumber: '',
		financeContactName: '',
		webAddress: '',
		brandName: '',
		ntnCNIC: '',
		gst: '',
		businessSector: '',
		products: '',
		bankAccountTitle: '',
		bankName: '',
		bankIBAN: '',
		bankCodeAccountNumber: '',
		pickupAddress: '',
		contactPerson: '',
		phoneNumber: '',
		emailAddress: '',
		password: '',
		confirmPassword: '',
		termsAccepted: false,
	});

	const [formErrors, setFormErrors] = useState<FormErrors>({});

	useEffect(() => {
		if (isAuthenticated) {
			navigate('/dashboard');
		}
	}, [isAuthenticated, navigate]);

	useEffect(() => {
		return () => {
			dispatch(clearError());
		};
	}, [dispatch]);

	// Dynamic background gradient based on theme
	const getBackgroundGradient = () => {
		return mode === 'dark' 
			? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
			: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
	};

	// Dynamic paper background based on theme
	const getPaperBackground = () => {
		return mode === 'dark' ? '#18191b' : '#ffffff';
	};

	// Dynamic border color based on theme
	const getBorderColor = () => {
		return mode === 'dark' ? '#333' : '#e0e0e0';
	};

	// Dynamic text color based on theme
	const getTextColor = () => {
		return mode === 'dark' ? 'grey.400' : 'grey.600';
	};

	// Dynamic input text color based on theme
	const getInputTextColor = () => {
		return mode === 'dark' ? 'white' : muiTheme.palette.text.primary;
	};

	const validateStep = (step: number): boolean => {
		const errors: FormErrors = {};

		switch (step) {
			case 0: // Business Information
				if (!formData.area) errors.area = 'Area is required';
				if (!formData.pointOfSale) errors.pointOfSale = 'Point of Sale is required';
				if (!formData.accountType) errors.accountType = 'Account Type is required';
				if (!formData.businessProfile) errors.businessProfile = 'Business Profile is required';
				break;
			case 1: // Client Details
				if (!formData.fullName) errors.fullName = 'Full Name is required';
				if (!formData.companyName) errors.companyName = 'Company Name is required';
				if (!formData.completeAddress) errors.completeAddress = 'Complete Address is required';
				if (!formData.authorizePersonName) errors.authorizePersonName = 'Authorize Person Name is required';
				if (!formData.authorizePersonDesignation) errors.authorizePersonDesignation = 'Authorize Person Designation is required';
				if (!formData.authorizePersonPhone) errors.authorizePersonPhone = 'Authorize Person Phone is required';
				if (!formData.authorizePersonCNIC) errors.authorizePersonCNIC = 'Authorize Person CNIC is required';
				if (!formData.adminContactName) errors.adminContactName = 'Admin Contact Name is required';
				if (!formData.adminPhoneNumber) errors.adminPhoneNumber = 'Admin Phone Number is required';
				if (!formData.financeContactName) errors.financeContactName = 'Finance Contact Name is required';
				if (!formData.brandName) errors.brandName = 'Brand Name is required';
				if (!formData.ntnCNIC) errors.ntnCNIC = 'NTN/CNIC is required';
				if (!formData.businessSector) errors.businessSector = 'Business Sector is required';
				if (!formData.products) errors.products = 'Products Description is required';
				break;
			case 2: // Bank Information
				if (!formData.bankAccountTitle) errors.bankAccountTitle = 'Bank Account Title is required';
				if (!formData.bankName) errors.bankName = 'Bank Name is required';
				if (!formData.bankIBAN) errors.bankIBAN = 'Bank IBAN is required';
				if (!formData.bankCodeAccountNumber) errors.bankCodeAccountNumber = 'Bank Code Account Number is required';
				break;
			case 3: // Operation Details
				if (!formData.pickupAddress) errors.pickupAddress = 'Pickup Address is required';
				if (!formData.contactPerson) errors.contactPerson = 'Contact Person is required';
				if (!formData.phoneNumber) errors.phoneNumber = 'Phone Number is required';
				if (!formData.emailAddress) {
					errors.emailAddress = 'Email Address is required';
				} else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
					errors.emailAddress = 'Please enter a valid email address';
				}
				break;
			case 4: // Account Setup
				if (!formData.password) {
					errors.password = 'Password is required';
				} else if (formData.password.length < 8) {
					errors.password = 'Password must be at least 8 characters';
				}
				if (!formData.confirmPassword) {
					errors.confirmPassword = 'Please confirm your password';
				} else if (formData.password !== formData.confirmPassword) {
					errors.confirmPassword = 'Passwords do not match';
				}
				if (!formData.termsAccepted) {
					errors.termsAccepted = 'You must accept the terms and conditions';
				}
				break;
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
		if (formErrors[name as keyof FormData]) {
			setFormErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleSelectChange = (e: any) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		if (formErrors[name as keyof FormData]) {
			setFormErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData(prev => ({
			...prev,
			termsAccepted: e.target.checked
		}));
		if (formErrors.termsAccepted) {
			setFormErrors(prev => ({
				...prev,
				termsAccepted: ''
			}));
		}
	};

	const handleNext = () => {
		if (validateStep(activeStep)) {
			setActiveStep((prevStep) => prevStep + 1);
		}
	};

	const handleBack = () => {
		setActiveStep((prevStep) => prevStep - 1);
	};

	const handleSubmit = async () => {
		if (validateStep(activeStep)) {
			// Prepare data for registration - send all form data
			const registrationData = {
				// Business Information
				area: formData.area,
				pointOfSale: formData.pointOfSale,
				accountType: formData.accountType,
				businessProfile: formData.businessProfile,
				
				// Client Information
				fullName: formData.fullName,
				companyName: formData.companyName,
				completeAddress: formData.completeAddress,
				authorizePersonName: formData.authorizePersonName,
				authorizePersonDesignation: formData.authorizePersonDesignation,
				authorizePersonPhone: formData.authorizePersonPhone,
				authorizePersonCNIC: formData.authorizePersonCNIC,
				adminContactName: formData.adminContactName,
				adminPhoneNumber: formData.adminPhoneNumber,
				financeContactName: formData.financeContactName,
				webAddress: formData.webAddress,
				brandName: formData.brandName,
				ntnCNIC: formData.ntnCNIC,
				gst: formData.gst,
				businessSector: formData.businessSector,
				products: formData.products,
				
				// Bank Details
				bankAccountTitle: formData.bankAccountTitle,
				bankName: formData.bankName,
				bankIBAN: formData.bankIBAN,
				bankCodeAccountNumber: formData.bankCodeAccountNumber,
				
				// Operation Information
				pickupAddress: formData.pickupAddress,
				contactPerson: formData.contactPerson,
				phoneNumber: formData.phoneNumber,
				emailAddress: formData.emailAddress,
				
				// Login Information
				password: formData.password,
				confirmPassword: formData.confirmPassword,
				termsAccepted: formData.termsAccepted,
			};
			
			await dispatch(registerUser(registrationData));
		}
	};

	const renderStepContent = (step: number) => {
		switch (step) {
			case 0:
				return (
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
					>
					<Typography variant="h5" sx={{ mb: 3, color: mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
						Business Information
					</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
							<Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 12px)' } }}>
								<FormControl fullWidth error={!!formErrors.area}>
									<InputLabel sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}>Area/Zone *</InputLabel>
									<Select
										name="area"
										value={formData.area}
										onChange={handleSelectChange}
										sx={{
											color: getInputTextColor(),
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: getBorderColor(),
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
										}}
									>
										{areas.map((area) => (
											<MenuItem key={area} value={area}>{area}</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<FormControl fullWidth error={!!formErrors.pointOfSale}>
									<InputLabel sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}>Point of Sale *</InputLabel>
									<Select
										name="pointOfSale"
										value={formData.pointOfSale}
										onChange={handleSelectChange}
										sx={{
											color: getInputTextColor(),
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: getBorderColor(),
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
										}}
									>
										{areas.map((area) => (
											<MenuItem key={area} value={area}>{area}</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<FormControl fullWidth error={!!formErrors.accountType}>
									<InputLabel sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}>Account Type *</InputLabel>
									<Select
										name="accountType"
										value={formData.accountType}
										onChange={handleSelectChange}
										sx={{
											color: getInputTextColor(),
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: getBorderColor(),
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
										}}
									>
										<MenuItem value="COD">COD</MenuItem>
										<MenuItem value="Non COD">Non COD</MenuItem>
									</Select>
								</FormControl>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<FormControl fullWidth error={!!formErrors.businessProfile}>
									<InputLabel sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}>Business Profile *</InputLabel>
									<Select
										name="businessProfile"
										value={formData.businessProfile}
										onChange={handleSelectChange}
										sx={{
											color: getInputTextColor(),
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: getBorderColor(),
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
										}}
									>
										<MenuItem value="Registered">Registered</MenuItem>
										<MenuItem value="Non Registered">Non Registered</MenuItem>
									</Select>
								</FormControl>
							</Box>
						</Box>
					</motion.div>
				);

			case 1:
				return (
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
					>
					<Typography variant="h5" sx={{ mb: 3, color: mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
						Client Details
					</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
							<Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Client's Full Name *"
									name="fullName"
									value={formData.fullName}
									onChange={handleInputChange}
									error={!!formErrors.fullName}
									helperText={formErrors.fullName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Company Name *"
									name="companyName"
									value={formData.companyName}
									onChange={handleInputChange}
									error={!!formErrors.companyName}
									helperText={formErrors.companyName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: '1 1 100%' }}>
								<TextField
									fullWidth
									label="Complete Address *"
									name="completeAddress"
									multiline
									rows={3}
									value={formData.completeAddress}
									onChange={handleInputChange}
									error={!!formErrors.completeAddress}
									helperText={formErrors.completeAddress}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Authorize Person Name *"
									name="authorizePersonName"
									value={formData.authorizePersonName}
									onChange={handleInputChange}
									error={!!formErrors.authorizePersonName}
									helperText={formErrors.authorizePersonName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Authorize Person Designation *"
									name="authorizePersonDesignation"
									value={formData.authorizePersonDesignation}
									onChange={handleInputChange}
									error={!!formErrors.authorizePersonDesignation}
									helperText={formErrors.authorizePersonDesignation}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Authorize Person Phone *"
									name="authorizePersonPhone"
									value={formData.authorizePersonPhone}
									onChange={handleInputChange}
									error={!!formErrors.authorizePersonPhone}
									helperText={formErrors.authorizePersonPhone}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Authorize Person CNIC *"
									name="authorizePersonCNIC"
									value={formData.authorizePersonCNIC}
									onChange={handleInputChange}
									error={!!formErrors.authorizePersonCNIC}
									helperText={formErrors.authorizePersonCNIC}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Admin Contact Name *"
									name="adminContactName"
									value={formData.adminContactName}
									onChange={handleInputChange}
									error={!!formErrors.adminContactName}
									helperText={formErrors.adminContactName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Admin Phone Number *"
									name="adminPhoneNumber"
									value={formData.adminPhoneNumber}
									onChange={handleInputChange}
									error={!!formErrors.adminPhoneNumber}
									helperText={formErrors.adminPhoneNumber}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Finance Contact Name *"
									name="financeContactName"
									value={formData.financeContactName}
									onChange={handleInputChange}
									error={!!formErrors.financeContactName}
									helperText={formErrors.financeContactName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Brand Name *"
									name="brandName"
									value={formData.brandName}
									onChange={handleInputChange}
									error={!!formErrors.brandName}
									helperText={formErrors.brandName}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="NTN/CNIC *"
									name="ntnCNIC"
									value={formData.ntnCNIC}
									onChange={handleInputChange}
									error={!!formErrors.ntnCNIC}
									helperText={formErrors.ntnCNIC}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="GST Number"
									name="gst"
									value={formData.gst}
									onChange={handleInputChange}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Business Sector *"
									name="businessSector"
									value={formData.businessSector}
									onChange={handleInputChange}
									error={!!formErrors.businessSector}
									helperText={formErrors.businessSector}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: '1 1 100%' }}>
								<TextField
									fullWidth
									label="Products Description *"
									name="products"
									multiline
									rows={2}
									value={formData.products}
									onChange={handleInputChange}
									error={!!formErrors.products}
									helperText={formErrors.products}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
						</Box>
					</motion.div>
				);

			case 2:
				return (
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
					>
					<Typography variant="h5" sx={{ mb: 3, color: mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
						Bank Information
					</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
							<Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Bank Account Title *"
									name="bankAccountTitle"
									value={formData.bankAccountTitle}
									onChange={handleInputChange}
									error={!!formErrors.bankAccountTitle}
									helperText={formErrors.bankAccountTitle}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<FormControl fullWidth error={!!formErrors.bankName}>
									<InputLabel sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}>Bank Name *</InputLabel>
									<Select
										name="bankName"
										value={formData.bankName}
										onChange={handleSelectChange}
										sx={{
											color: getInputTextColor(),
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: getBorderColor(),
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: 'secondary.main',
											},
										}}
									>
										{banks.map((bank) => (
											<MenuItem key={bank} value={bank}>{bank}</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Bank IBAN *"
									name="bankIBAN"
									value={formData.bankIBAN}
									onChange={handleInputChange}
									error={!!formErrors.bankIBAN}
									helperText={formErrors.bankIBAN}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Bank Code Account Number *"
									name="bankCodeAccountNumber"
									value={formData.bankCodeAccountNumber}
									onChange={handleInputChange}
									error={!!formErrors.bankCodeAccountNumber}
									helperText={formErrors.bankCodeAccountNumber}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
						</Box>
					</motion.div>
				);

			case 3:
				return (
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
					>
					<Typography variant="h5" sx={{ mb: 3, color: mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600 }}>
						Operation Details
					</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
							<Box sx={{ flex: '1 1 100%' }}>
								<TextField
									fullWidth
									label="Pickup Address *"
									name="pickupAddress"
									multiline
									rows={3}
									value={formData.pickupAddress}
									onChange={handleInputChange}
									error={!!formErrors.pickupAddress}
									helperText={formErrors.pickupAddress}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Contact Person *"
									name="contactPerson"
									value={formData.contactPerson}
									onChange={handleInputChange}
									error={!!formErrors.contactPerson}
									helperText={formErrors.contactPerson}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Phone Number *"
									name="phoneNumber"
									value={formData.phoneNumber}
									onChange={handleInputChange}
									error={!!formErrors.phoneNumber}
									helperText={formErrors.phoneNumber}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: '1 1 100%' }}>
								<TextField
									fullWidth
									label="Email Address *"
									name="emailAddress"
									type="email"
									value={formData.emailAddress}
									onChange={handleInputChange}
									error={!!formErrors.emailAddress}
									helperText={formErrors.emailAddress}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
						</Box>
					</motion.div>
				);

			case 4:
				return (
					<motion.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
					>
					<Typography variant="h5" sx={{ mb: 3, color: mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600 }}>
						Account Setup
					</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Password *"
									name="password"
									type={showPassword ? 'text' : 'password'}
									value={formData.password}
									onChange={handleInputChange}
									error={!!formErrors.password}
									helperText={formErrors.password}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => setShowPassword(!showPassword)}
													edge="end"
													sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
								<TextField
									fullWidth
									label="Confirm Password *"
									name="confirmPassword"
									type={showConfirmPassword ? 'text' : 'password'}
									value={formData.confirmPassword}
									onChange={handleInputChange}
									error={!!formErrors.confirmPassword}
									helperText={formErrors.confirmPassword}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => setShowConfirmPassword(!showConfirmPassword)}
													edge="end"
													sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary' }}
												>
													{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											'& fieldset': { borderColor: getBorderColor() },
											'&:hover fieldset': { borderColor: 'secondary.main' },
											'&.Mui-focused fieldset': { borderColor: 'secondary.main' },
										},
										'& .MuiInputLabel-root': { color: mode === 'dark' ? 'grey.400' : 'text.secondary' },
										'& .MuiInputBase-input': { color: getInputTextColor() },
									}}
								/>
							</Box>
							<Box sx={{ flex: '1 1 100%' }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.termsAccepted}
											onChange={handleCheckboxChange}
											sx={{
												color: 'secondary.main',
												'&.Mui-checked': {
													color: 'secondary.main',
												},
											}}
										/>
									}
									label={
										<Typography sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary', fontSize: '0.875rem' }}>
											I agree to all the{' '}
											<Link 
												to="/terms-and-conditions" 
												style={{ 
													color: '#bc2222', 
													textDecoration: 'none',
													fontWeight: 600
												}}
												onMouseEnter={(e) => {
													(e.target as HTMLElement).style.textDecoration = 'underline';
												}}
												onMouseLeave={(e) => {
													(e.target as HTMLElement).style.textDecoration = 'none';
												}}
											>
												terms and conditions
											</Link>{' '}
											*
										</Typography>
									}
								/>
								{formErrors.termsAccepted && (
									<Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
										{formErrors.termsAccepted}
									</Typography>
								)}
							</Box>
						</Box>
					</motion.div>
				);

			default:
				return null;
		}
	};

	return (
		<Box sx={{ 
			minHeight: '100vh', 
			display: 'flex', 
			alignItems: 'center', 
			justifyContent: 'center',
			p: { xs: 2, sm: 3 },
            pt: { xs: 5 },
			background: getBackgroundGradient()
		}}>
			<motion.div
				initial={{ opacity: 0, y: 40 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
			>
				<Paper
					sx={{
						p: { xs: 3, sm: 4, md: 6 },
                        mt: { xs: 5, md: 10 },
						borderRadius: 4,
						bgcolor: getPaperBackground(),
						border: `1px solid ${getBorderColor()}`,
						textAlign: 'center',
						width: '100%',
                        maxWidth: { xs: '100%', sm: '90vw', md: '80vw', lg: '70vw' },
						boxShadow: mode === 'dark' 
							? '0 20px 40px rgba(0,0,0,0.3)' 
							: '0 20px 40px rgba(0,0,0,0.1)'
					}}
				>
					<Typography variant="h3" sx={{ 
						fontWeight: 700, 
						color: 'secondary.main', 
						mb: 2,
						fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' }
					}}>
						Business Registration
					</Typography>
					
					<Typography variant="body1" sx={{ 
						color: getTextColor(), 
						mb: 4,
						lineHeight: 1.6,
						fontSize: { xs: '0.875rem', sm: '1rem' }
					}}>
						Complete your business registration in a few simple steps
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
							{error}
						</Alert>
					)}

					<Box sx={{ display: { xs: 'none', md: 'block' } }}>
						<Stepper 
							activeStep={activeStep} 
							sx={{ 
								mb: 4,
								'& .MuiStepLabel-root .Mui-completed': {
									color: 'secondary.main',
								},
								'& .MuiStepLabel-root .Mui-active': {
									color: 'secondary.main',
								},
								'& .MuiStepLabel-label': {
									color: mode === 'dark' ? 'grey.400' : 'text.secondary',
									fontSize: { xs: '0.75rem', sm: '0.875rem' },
									'&.Mui-active': {
										color: 'secondary.main',
									},
									'&.Mui-completed': {
										color: 'secondary.main',
									},
								},
							}}
						>
							{steps.map((label) => (
								<Step key={label}>
									<StepLabel>{label}</StepLabel>
								</Step>
							))}
						</Stepper>
					</Box>
					
					{/* Mobile Step Indicator */}
					<Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4 }}>
						<Box sx={{ 
							display: 'flex', 
							alignItems: 'center', 
							justifyContent: 'space-between',
							mb: 2
						}}>
							<Typography variant="body2" sx={{ color: mode === 'dark' ? 'grey.400' : 'text.secondary', fontSize: '0.875rem' }}>
								Step {activeStep + 1} of {steps.length}
							</Typography>
							<Typography variant="body2" sx={{ color: 'secondary.main', fontSize: '0.875rem', fontWeight: 600 }}>
								{steps[activeStep]}
							</Typography>
						</Box>
						<Box sx={{ 
							display: 'flex', 
							gap: 1,
							mb: 2
						}}>
							{steps.map((_, index) => (
								<Box
									key={index}
									sx={{
										height: 4,
										flex: 1,
										borderRadius: 2,
										bgcolor: index <= activeStep ? 'secondary.main' : (mode === 'dark' ? '#333' : 'grey.300'),
										transition: 'background-color 0.3s ease'
									}}
								/>
							))}
						</Box>
					</Box>

					<Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left', mb: 4 }}>
						<AnimatePresence mode="wait">
							{renderStepContent(activeStep)}
						</AnimatePresence>
					</Box>

					<Box sx={{ 
						display: 'flex', 
						flexDirection: { xs: 'column', sm: 'row' },
						justifyContent: 'space-between', 
						alignItems: 'center',
						gap: { xs: 2, sm: 0 }
					}}>
						<Button
							disabled={activeStep === 0}
							onClick={handleBack}
							startIcon={<ArrowBack />}
							sx={{
								color: mode === 'dark' ? 'grey.400' : 'text.secondary',
								'&:hover': {
									color: 'secondary.main',
								},
								'&:disabled': {
									color: mode === 'dark' ? 'grey.600' : 'grey.400',
								},
								order: { xs: 2, sm: 1 }
							}}
						>
							Back
						</Button>

						{activeStep === steps.length - 1 ? (
							<Button
								variant="contained"
								size="large"
								disabled={loading}
								onClick={handleSubmit}
								endIcon={<ArrowForward />}
								sx={{
									bgcolor: 'secondary.main',
									color: 'white',
									px: { xs: 3, sm: 4 },
									py: 1.5,
									fontSize: { xs: 14, sm: 16 },
									fontWeight: 600,
									width: { xs: '100%', sm: 'auto' },
									'&:hover': {
										bgcolor: 'secondary.dark',
									},
									'&:disabled': {
										bgcolor: 'grey.600',
										color: 'grey.400',
									},
									order: { xs: 1, sm: 2 }
								}}
							>
								{loading ? 'Creating Account...' : 'Create Account'}
							</Button>
						) : (
							<Button
								variant="contained"
								size="large"
								onClick={handleNext}
								endIcon={<ArrowForward />}
								sx={{
									bgcolor: 'secondary.main',
									color: 'white',
									px: { xs: 3, sm: 4 },
									py: 1.5,
									fontSize: { xs: 14, sm: 16 },
									fontWeight: 600,
									width: { xs: '100%', sm: 'auto' },
									'&:hover': {
										bgcolor: 'secondary.dark',
									},
									order: { xs: 1, sm: 2 }
								}}
							>
								Next
							</Button>
						)}
					</Box>

					<Box sx={{ mt: 4 }}>
						<Typography variant="body2" sx={{ color: mode === 'dark' ? 'grey.500' : 'text.secondary', mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
							Already have an account?
						</Typography>
						<Button
							component={Link}
							to="/login"
							sx={{
								color: 'secondary.main',
								textDecoration: 'none',
								fontSize: { xs: '0.875rem', sm: '1rem' },
								'&:hover': {
									color: 'secondary.dark',
								},
							}}
						>
							Sign In
						</Button>
					</Box>
				</Paper>
			</motion.div>
		</Box>
	);
};

export default Register; 